/**
 * نزدیک‌ترین ردیف به یک نقطه، روی کره‌ی زمین.
 *
 * جدا از geoRepo نشسته و عمداً هیچ چیزی import نمی‌کند — نه supabase، نه
 * چیزِ دیگر. دو دلیل: (۱) این تنها تکه‌ی جریانِ «شهرم را پیدا کن» است که
 * می‌تواند **بی‌صدا** غلط باشد و باید تست شود، و تستِ node نمی‌تواند
 * ماژولی را بار کند که به کلاینتِ supabase وصل است؛ (۲) هندسه ربطی به
 * دیتابیس ندارد.
 */

/**
 * فرمول haversine است نه فاصله‌ی اقلیدسی روی درجه‌ها. آن یکی در عرض‌های
 * بالا شهرِ اشتباه را برمی‌گرداند، چون یک درجه طول آن‌جا خیلی کوتاه‌تر از
 * یک درجه عرض است — در عرضِ ۴۵ درجه حدودِ ۰.۷ برابر، در عرضِ ۶۰ درجه نصف.
 */
export function distanceKm(
  aLat: number, aLng: number, bLat: number, bLng: number,
): number {
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(bLat - aLat);
  const dLng = rad(bLng - aLng);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(rad(aLat)) * Math.cos(rad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(a)));
}

export function pickNearest<T extends { lat: number | null; lng: number | null }>(
  lat: number, lng: number, rows: T[],
): T | null {
  let best: T | null = null;
  let bestD = Infinity;
  for (const r of rows) {
    if (r.lat == null || r.lng == null) continue;
    const d = distanceKm(lat, lng, r.lat, r.lng);
    if (d < bestD) { bestD = d; best = r; }
  }
  return best;
}

/**
 * باید این‌قدر بزرگ‌تر باشد تا حرفِ نزدیک‌ترین را پس بزند.
 *
 * عددش با داده‌ی واقعی کوک شده، نه از سرِ حدس. سه موردی که تعیینش کردند:
 *   • کاناتا/اتاوا  = ۱۰.۰  ← باید جذب شود (محله است)
 *   • نپیان/اتاوا   = ۵.۷   ← باید جذب شود (محله است)
 *   • آژاکس/اسکاربورو = ۵.۰ ← نباید (شهرداریِ مستقلِ ۱۲۷ هزارنفری)
 * پنجره تنگ است و همین می‌گوید این قاعده تا کجا می‌تواند برسد: جمعیت به
 * تنهایی «محله» را از «شهرِ کوچکِ کنارِ کلان‌شهر» جدا نمی‌کند. پاکسازیِ خودِ
 * فهرست کارِ دیگری است.
 */
export const DOMINANT_RATIO = 5;

/**
 * تا چند کیلومتری، یک شهر «همان‌جا»ست و نه یک سفر — و این به اندازه‌اش
 * بستگی دارد، نه یک عددِ ثابت.
 *
 * اولش شعاعِ ثابتِ ۳۰ کیلومتر بود و تستِ کارلتون‌پلیس گرفتش: آن شهرِ واقعیِ
 * ۱۲ هزار نفری ۲۷ کیلومتریِ کاناتاست، پس با شعاعِ ثابت «کاناتایی» می‌شد —
 * یعنی جذبِ جایی که خودش شهر نیست. یک محله‌ی صدهزارنفری دامنه‌ی یک
 * کلان‌شهرِ میلیونی را ندارد.
 *
 * ریشه‌ی دوم است چون سطحِ شهر با جمعیت رشد می‌کند نه شعاعش؛ ضریب طوری
 * انتخاب شده که اتاوا (۱.۰۲ م) تا ۳۰ کیلومتر برسد — کاناتا در ۲۲
 * کیلومتری‌اش — ولی خودِ کاناتا (۱۰۲ هزار) فقط تا ۹.۶ کیلومتر.
 */
export function dominanceKm(population: number): number {
  return Math.min(DOMINANT_MAX_KM, Math.max(5, 0.03 * Math.sqrt(Math.max(0, population))));
}

/**
 * سقفِ دامنه، هرچقدر هم شهر بزرگ باشد.
 *
 * بدونِ سقف، ریشه‌ی دوم برای برلین و تورنتو به ۵۰ تا ۵۵ کیلومتر می‌رسید و
 * آن‌وقت شهرهای واقعیِ حاشیه را می‌بلعید — پوتسدام (۲۵ کیلومتریِ برلین،
 * ۱۸۷ هزار نفر، پایتختِ براندنبورگ) و آرورا و آژاکس در حاشیه‌ی تورنتو.
 *
 * ۲۵ کیلومتر جایی است که کاناتا (۱۹ کیلومتریِ اتاوا) داخلش می‌ماند ولی
 * پوتسدام بیرون. آژاکس و آرورا را نه این سقف که {@link DOMINANT_RATIO}
 * نگه می‌دارد.
 */
export const DOMINANT_MAX_KM = 25;

/**
 * شهری که کاربر **می‌گوید** اهلش است — که همیشه نزدیک‌ترین نقطه‌ی فهرست نیست.
 *
 * دیتاست (dr5hn) محله‌های ادغام‌شده را هنوز شهرِ جدا نگه داشته: کاناتا و
 * نپیان و وَنیه از ۲۰۰۱ بخشی از اتاوا هستند ولی هر سه ردیفِ مستقل دارند. پس
 * «نزدیک‌ترین» برای کسی که در کاناتاست «کاناتا» می‌داد — جایی که روی هیچ
 * فهرستِ رخدادی وجود ندارد و کاربر هم خودش را اهلِ اتاوا می‌داند.
 *
 * قاعده: اگر شهری هست که دستِ‌کم {@link DOMINANT_RATIO} برابرِ نزدیک‌ترین
 * جمعیت دارد **و** کاربر داخلِ دامنه‌اش است ({@link dominanceKm})، همان
 * انتخاب می‌شود. وگرنه نزدیک‌ترین سرِ جایش می‌ماند.
 *
 * چرا نسبت، و نه صرفاً «بزرگ‌ترینِ اطراف»: گاتینو ۶ کیلومتریِ اتاواست و شهرِ
 * واقعیِ خودش است (استانِ دیگر، شهرداریِ دیگر). «بزرگ‌ترین در شعاع» هر
 * گاتینویی را اتاوایی می‌کرد. با نسبتِ ۴، اتاوا فقط ۳.۵ برابرِ گاتینوست و
 * دست بهش نمی‌زند — ولی ۱۰ برابرِ کاناتاست و جایش را می‌گیرد.
 *
 * ⚠ این فقط **حدسِ اولیه‌ی** آن دکمه است، نه حقیقت: کاربر فیلد را جلوی خودش
 * می‌بیند و می‌تواند عوضش کند. برای همین هم این قاعده این‌جاست و نه روی
 * خودِ فهرستِ شهرها — همان قاعده اگر ردیف‌ها را حذف می‌کرد، شهرهای واقعی
 * را هم می‌برد (آژاکس و آرورا در انتاریو، هر دو شهرِ مستقل).
 */
export function pickHomeCity<
  T extends { lat: number | null; lng: number | null; population?: number | null },
>(lat: number, lng: number, rows: T[]): T | null {
  /**
   * ردیف‌های بی‌جمعیت کنار گذاشته می‌شوند، چون **لنگرِ** این قاعده‌اند.
   *
   * دیتاست ردیف‌هایی با `population = 0` دارد (نامِ ناحیه‌ها و نقطه‌های
   * بی‌سکنه — مثلاً «York» یک کیلومتریِ آرورا). اگر یکی از آن‌ها نزدیک‌ترین
   * می‌شد، نسبتِ جمعیت به صفر تقسیم می‌شد و عملاً هر شهرِ بزرگ‌تری در شعاع
   * برنده بود: آرورا — شهرِ واقعیِ ۵۵ هزارنفری — «نیومارکت» می‌شد، فقط
   * چون یک ردیفِ خالی یک کیلومتر نزدیک‌تر بود.
   */
  const real = rows.filter((r) => (r.population ?? 0) > 0);
  const pool = real.length > 0 ? real : rows;

  const near = pickNearest(lat, lng, pool);
  if (!near) return near;

  const nearPop = near.population ?? 0;
  let best = near;
  let bestPop = nearPop;
  for (const r of pool) {
    if (r === near || r.lat == null || r.lng == null) continue;
    const pop = r.population ?? 0;
    if (pop <= bestPop || pop < nearPop * DOMINANT_RATIO) continue;
    if (distanceKm(lat, lng, r.lat, r.lng) > dominanceKm(pop)) continue;
    best = r;
    bestPop = pop;
  }
  return best;
}
